import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import dayjs, { Dayjs } from "dayjs";
import { getUniqueId } from "../../utils/unique-id";
import { DATE_ISO } from "../date-picker/constants";
import { DateRangeResult } from "./interfaces/date-range-result";

const baseClass = "ux-date-range-picker";

@Component({
    tag: "ux-date-range-picker",
    styleUrl: "date-range-picker.scss",
    shadow: false
})
export class DateRangePicker implements ComponentInterface {
    /**
     * The component DOM node
     */
    @Element() domNode!: HTMLUxDateRangePickerElement;

    /**
     * Format of the date value
     */
    @Prop() readonly dateFormat: string = "YYYY-MM-DD";
    /**
     * Flag that controls if the field is disabled
     */
    @Prop() readonly disabled: boolean = false;
    /**
     * The weekday the calendar starts with (0=sunday, 1=monday, 6=saturday)
     */
    @Prop() readonly firstWeekday: number = 1;
    /**
     * Input id attribute
     */
    /* eslint-disable-next-line */
    @Prop() inputId?: string;
    /**
     * Controls the appearance of the control
     */
    @Prop() readonly kind: "default" | "form" = "default";
    /**
     * The language in which texts are displayed
     */
    @Prop() readonly language?: string;
    /**
     * Optionally, the date can be restricted to a maximum value (YYYY-MM-DD)
     */
    @Prop() readonly maxDate?: string;
    /**
     * Optionally, the date can be restricted to a minimum value (YYYY-MM-DD)
     */
    @Prop() readonly minDate?: string;
    /**
     * The input placeholder text
     */
    @Prop() readonly placeholder?: string;
    /**
     * Label for the reset button
     */
    @Prop() readonly resetText?: string;
    /**
     * Label for the today button
     */
    @Prop() readonly todayText?: string;

    /**
     * Label text for the range start field
     */
    @Prop() readonly rangeStartLabel?: string;
    /**
     * Label text for the range start field
     */
    @Prop() readonly rangeStartTopLabel?: string;
    /**
     * Name for the range start input
     */
    @Prop() readonly rangeStartName: string = "start_date";
    /**
     * Initial range start date (YYYY-MM-DD)
     */
    @Prop() readonly rangeStartValue: string = "";
    /**
     * Flag that controls if the range start is mandatory
     */
    @Prop() readonly rangeStartRequired: boolean = false;
    /**
     * Indicates an invalid start date
     */
    @Prop() readonly rangeStartError: boolean = false;
    /**
     * Error message for the range start field
     */
    @Prop() readonly rangeStartErrorMsg: string | null = null;

    /**
     * Label text for the range end field
     */
    @Prop() readonly rangeEndLabel?: string;
    /**
     * Label text for the range end field
     */
    @Prop() readonly rangeEndTopLabel?: string;
    /**
     * Name for the range end input
     */
    @Prop() readonly rangeEndName: string = "end_date";
    /**
     * Initial range end date (YYYY-MM-DD)
     */
    @Prop() readonly rangeEndValue: string = "";
    /**
     * Flag that controls if the range end is mandatory
     */
    @Prop() readonly rangeEndRequired: boolean = false;
    /**
     * Indicates an invalid end date
     */
    @Prop() readonly rangeEndError: boolean = false;
    /**
     * Error message for the range end field
     */
    @Prop() readonly rangeEndErrorMsg: string | null = null;

    /**
     * Storage for the range start day
     */
    @State() rangeStartDay: Dayjs | null = null;
    /**
     * Storage for the range end day
     */
    @State() rangeEndDay: Dayjs | null = null;

    private startDatePicker: HTMLUxDatePickerElement | null = null;
    private endDatePicker: HTMLUxDatePickerElement | null = null;

    /**
     * Event emitted when the dates in the date range picker are changed.
     * The keys contain a string with the formatted date if a correct date has been selected,
     * otherwise they are set to null.
     */
    @Event({
        eventName: "uxDateRangeChange"
    })
    dateRangeChange!: EventEmitter<{
        startDate: string | null;
        endDate: string | null;
    }>;

    private emitRangeChangeEvent = () => {
        this.dateRangeChange.emit({
            startDate: this.rangeStartDay !== null ? this.rangeStartDay.format(DATE_ISO) : null,
            endDate: this.rangeEndDay !== null ? this.rangeEndDay.format(DATE_ISO) : null
        });
    };

    @Watch("inputId")
    setInputId() {
        this.inputId = this.inputId || getUniqueId();
    }

    @Watch("rangeStartValue")
    setInitialRangeStart() {
        if (this.rangeStartValue !== "") {
            const startDate = dayjs(this.rangeStartValue, DATE_ISO);
            if (startDate.isValid()) {
                this.rangeStartDay = startDate;
                return;
            }
        }
        this.rangeStartDay = null;
    }

    @Watch("rangeEndValue")
    setInitialRangeEnd() {
        if (this.rangeEndValue !== "") {
            const endDate = dayjs(this.rangeEndValue, DATE_ISO);
            if (endDate.isValid()) {
                this.rangeEndDay = endDate;
                return;
            }
        }
        this.rangeEndDay = null;
    }

    @Listen("uxDateSelect")
    // @ts-expect-error Check for typing
    handleDateSelect(event) {
        const { value } = event.detail;
        const datePicker = event.target;
        const { startDatePicker, endDatePicker } = this;
        const activeDay = value === "" ? null : dayjs(value);
        const range: DateRangeResult = this.getActiveRange(datePicker, activeDay);
        if (range.flipped) {
            event.preventDefault();
        }
        this.rangeStartDay = range.start;
        this.rangeEndDay = range.end;
        if (this.rangeStartDay !== null) {
            // @ts-expect-error Check for typing
            startDatePicker.selectDate(this.rangeStartDay.format(DATE_ISO));
        }
        if (this.rangeEndDay !== null) {
            // @ts-expect-error Check for typing
            endDatePicker.selectDate(this.rangeEndDay.format(DATE_ISO));
        }
        this.emitRangeChangeEvent();
    }

    @Listen("uxDateHover")
    // @ts-expect-error Check for typing
    handleDateHover(event) {
        const { value } = event.detail;
        const datePicker = event.target;
        const activeDay = value === "" ? null : dayjs(value);
        const range: DateRangeResult = this.getActiveRange(datePicker, activeDay);
        datePicker.rangeStart = range.start === null ? "" : range.start.format(DATE_ISO);
        datePicker.rangeEnd = range.end === null ? "" : range.end.format(DATE_ISO);
    }

    private getActiveRange(datePicker: HTMLUxDatePickerElement, activeDay: Dayjs | null): DateRangeResult {
        const { startDatePicker, endDatePicker } = this;
        const range: DateRangeResult = {
            start: this.rangeStartDay,
            end: this.rangeEndDay,
            flipped: false
        };
        if (!activeDay?.isValid()) {
            if (datePicker === startDatePicker) {
                range.start = null;
            } else if (datePicker === endDatePicker) {
                range.end = null;
            }
            return range;
        }
        if (datePicker === startDatePicker) {
            if (range.end !== null && activeDay.isAfter(range.end, "d")) {
                range.end = activeDay;
                range.flipped = true;
            } else {
                range.start = activeDay;
            }
        } else if (datePicker === endDatePicker) {
            if (range.start !== null && activeDay.isBefore(range.start, "d")) {
                range.end = range.start;
                range.start = activeDay;
                range.flipped = true;
            } else {
                range.end = activeDay;
            }
        }
        return range;
    }

    componentWillLoad() {
        this.setInputId();
        this.setInitialRangeStart();
        this.setInitialRangeEnd();
    }

    render() {
        const {
            dateFormat,
            firstWeekday,
            inputId,
            kind,
            language,
            maxDate,
            minDate,
            placeholder,
            rangeStartDay,
            rangeStartLabel,
            rangeStartTopLabel,
            rangeStartName,
            rangeStartValue,
            rangeStartRequired,
            rangeStartError,
            rangeStartErrorMsg,
            rangeEndDay,
            rangeEndTopLabel,
            rangeEndLabel,
            rangeEndName,
            rangeEndValue,
            rangeEndRequired,
            rangeEndError,
            rangeEndErrorMsg,
            todayText,
            resetText
        } = this;

        const rangeStart = rangeStartDay !== null ? rangeStartDay.format(DATE_ISO) : "";
        const rangeEnd = rangeEndDay !== null ? rangeEndDay.format(DATE_ISO) : "";

        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--kind-default`]: kind === "default",
                    [`${baseClass}--kind-form`]: kind === "form"
                }}
            >
                <div class={`${baseClass}__container`}>
                    <ux-date-picker
                        date-format={dateFormat}
                        first-weekday={firstWeekday}
                        value={rangeStartValue}
                        input-id={`${inputId}-start`}
                        kind={kind}
                        top-label={rangeStartTopLabel}
                        label={rangeStartLabel}
                        language={language}
                        max-date={maxDate}
                        min-date={minDate}
                        name={rangeStartName}
                        placeholder={placeholder}
                        range-start={rangeStart}
                        range-end={rangeEnd}
                        required={rangeStartRequired}
                        error={rangeStartError}
                        error-msg={rangeStartErrorMsg}
                        reset-text={resetText}
                        today-text={todayText}
                        ref={(el) => (el ? (this.startDatePicker = el) : null)}
                    />
                    <span class={`${baseClass}__separator`}>-</span>
                    <ux-date-picker
                        date-format={dateFormat}
                        first-weekday={firstWeekday}
                        value={rangeEndValue}
                        input-id={`${inputId}-end`}
                        kind={kind}
                        top-label={rangeEndTopLabel}
                        label={rangeEndLabel}
                        language={language}
                        max-date={maxDate}
                        min-date={minDate}
                        name={rangeEndName}
                        placeholder={placeholder}
                        range-start={rangeStart}
                        range-end={rangeEnd}
                        required={rangeEndRequired}
                        error={rangeEndError}
                        error-msg={rangeEndErrorMsg}
                        reset-text={resetText}
                        today-text={todayText}
                        ref={(el) => (el ? (this.endDatePicker = el) : null)}
                    />
                </div>
            </Host>
        );
    }
}
